import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Card,
  CardBody,
  Input,
  Spinner,
  Badge,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import api from "../helpers/api";
import PermissionGuard from "../components/Common/PermissionGuard";
import ContractFormModal from "../components/ContractFormModal";
import ContractDetailModal from "../components/ContractDetailModal";
import usePermission from "../Hooks/usePermission";

const formatDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const STATUS_BADGE = {
  UPCOMING: { color: "info", labelKey: "contracts.status_upcoming", fallback: "Upcoming" },
  ACTIVE: { color: "success", labelKey: "contracts.status_active", fallback: "Active" },
  EXPIRING_SOON: { color: "warning", labelKey: "contracts.status_expiring_soon", fallback: "Expiring Soon" },
  EXPIRED: { color: "danger", labelKey: "contracts.status_expired", fallback: "Expired" },
};

const Contracts = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  // One currency per org (no FX conversion) — values are always shown in the org's
  // current currency, not a per-contract snapshot. See currency-uniformity work.
  const orgCurrency = useSelector((s) => s.ActiveContext?.activeOrganizationCurrency) || "USD";

  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailContract, setDetailContract] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const openDetail = (contract) => {
    setDetailContract(contract);
    setDetailModalOpen(true);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategoryId) params.set("contractCategoryId", filterCategoryId);
      if (filterStatus) params.set("status", filterStatus);
      if (searchTerm) params.set("search", searchTerm);
      const { data } = await api.get(`contract/get-all?${params.toString()}`);
      setContracts(data.response || []);
    } catch (e) {
      console.error("Failed to load contracts:", e);
    } finally {
      setLoading(false);
    }
  }, [filterCategoryId, filterStatus, searchTerm]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("organization-contract-category/get-all");
      setCategories(data.response || []);
    } catch (e) {
      console.error("Failed to load categories:", e);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    const timer = setTimeout(() => reload(), 300);
    return () => clearTimeout(timer);
  }, [reload]);

  const handleOpenCreate = () => {
    setEditingContract(null);
    setFormModalOpen(true);
  };

  const handleOpenEdit = (contract) => {
    setEditingContract(contract);
    setFormModalOpen(true);
  };

  const handleSaved = () => {
    setFormModalOpen(false);
    setEditingContract(null);
    reload();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`contract/delete/${deleteTarget.id}`);
      setDeleteTarget(null);
      reload();
    } catch (err) {
      setDeleteError(
        err?.response?.data?.message || t("contracts.delete_failed", "Failed to delete contract")
      );
    } finally {
      setDeleting(false);
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: "", label: t("contracts.all_statuses", "All statuses") },
      ...Object.keys(STATUS_BADGE).map((s) => ({ value: s, label: t(STATUS_BADGE[s].labelKey, STATUS_BADGE[s].fallback) })),
    ],
    [t]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-3">
          <PermissionGuard permission="maintenance:create">
            <button type="button" className="btn btn-soft-success d-flex align-items-center gap-2" onClick={handleOpenCreate}>
              <FeatherIcon icon="plus" size={16} />
              {t("contracts.add_contract", "Add Contract")}
            </button>
          </PermissionGuard>
        </div>

        <Card className="mb-3">
          <CardBody>
            <Row className="g-2 align-items-center">
              <Col md={4}>
                <Input
                  type="text"
                  placeholder={t("contracts.search_placeholder", "Search by contract number...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  value={filterCategoryId}
                  onChange={(e) => setFilterCategoryId(e.target.value)}
                >
                  <option value="">{t("contracts.all_categories", "All categories")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-5 text-muted">
                {t("contracts.empty", "No contracts found")}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>{t("contracts.contract_number_full", "Contract Number")}</th>
                      <th>{t("contracts.category", "Category")}</th>
                      <th>{t("contracts.assets", "Assets")}</th>
                      <th>{t("contracts.start_date", "Start Date")}</th>
                      <th>{t("contracts.end_date", "End Date")}</th>
                      <th>{t("contracts.value", "Value")}</th>
                      <th>{t("contracts.status", "Status")}</th>
                      <th>{t("shared.actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {contracts.map((c) => {
                        const badge = STATUS_BADGE[c.status] || STATUS_BADGE.ACTIVE;
                        return (
                          <motion.tr
                            key={c.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                          >
                            <td>
                              <span
                                className="text-primary"
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => openDetail(c)}
                              >
                                {c.contractNumber}
                              </span>
                            </td>
                            <td>{c.categoryName || "-"}</td>
                            <td>{c.assets?.length ? `${c.assets.length} ${t("contracts.assets_short", "assets")}` : "-"}</td>
                            <td>{formatDDMMYYYY(c.startDate) || "-"}</td>
                            <td>{formatDDMMYYYY(c.endDate) || "-"}</td>
                            <td>
                              {c.contractValue != null
                                ? `${c.contractValue} ${orgCurrency}`.trim()
                                : "-"}
                            </td>
                            <td>
                              <Badge color={badge.color}>{t(badge.labelKey, badge.fallback)}</Badge>
                            </td>
                            <td>
                              <PermissionGuard permission="maintenance:update">
                                <span className="text-warning me-2" role="button" onClick={() => handleOpenEdit(c)}>
                                  <FeatherIcon icon="edit" size={16} />
                                </span>
                              </PermissionGuard>
                              <PermissionGuard permission="maintenance:delete">
                                <span
                                  className="text-danger"
                                  role="button"
                                  onClick={() => {
                                    setDeleteError("");
                                    setDeleteTarget(c);
                                  }}
                                >
                                  <FeatherIcon icon="trash-2" size={16} />
                                </span>
                              </PermissionGuard>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <ContractFormModal
          isOpen={formModalOpen}
          toggle={() => setFormModalOpen(false)}
          contract={editingContract}
          categories={categories}
          onSaved={handleSaved}
        />

        <ContractDetailModal
          isOpen={detailModalOpen}
          toggle={() => setDetailModalOpen(false)}
          contract={detailContract}
          onEdit={hasPermission("maintenance:update") ? (c) => {
            setDetailModalOpen(false);
            handleOpenEdit(c);
          } : null}
        />

        <Modal isOpen={!!deleteTarget} toggle={() => !deleting && setDeleteTarget(null)}>
          <ModalHeader toggle={() => !deleting && setDeleteTarget(null)}>
            {t("shared.confirm_delete", "Confirm Delete")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">
              {t("contracts.delete_confirmation", {
                number: deleteTarget?.contractNumber,
                defaultValue:
                  'Are you sure you want to delete contract "{{number}}"? This action cannot be undone.',
              })}
            </p>
            {deleteError && <Alert color="danger" className="mt-3 mb-0">{deleteError}</Alert>}
          </ModalBody>
          <ModalFooter>
            <Button color="light" outline onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("shared.cancel", "Cancel")}
            </Button>
            <Button color="danger" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? t("shared.processing", "Processing...") : t("shared.delete", "Delete")}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default Contracts;
